/**
 * FEAT-102 / S-413 — the post drawer's image picker, as pure logic.
 *
 * The API decides what a post may use (GET /social-calendar/:id/image-options:
 * its piece's images and its own uploads — BUG-101). This shapes that list for
 * the thumbnails and decides what a pick saves.
 */

export type ImageOptionSource = "art_director" | "press_kit" | "upload";

export interface ImageOption {
  file_id: string;
  source: ImageOptionSource;
  label: string;
  /** The asset proxy path, relative to the API (`/assets/<id>`). */
  url: string;
}

export interface ImageOptionsResponse {
  selected_file_id: string | null;
  options: ImageOption[];
}

export interface PickerOption {
  fileId: string;
  /** "current" — the post's image is not among the options (chosen before the rule). */
  source: ImageOptionSource | "current";
  label: string;
  thumbUrl: string;
  selected: boolean;
}

const THUMB_WIDTH = 192;

function thumb(apiBase: string, path: string): string {
  return `${apiBase}${path}?width=${THUMB_WIDTH}`;
}

/**
 * Every option, in the API's order, with `selectedFileId` marked. An image the
 * post already carries but that is not an option stays visible (first, as
 * "Current"): the server still accepts it, and hiding it would look as if the
 * post had no image.
 */
export function pickerOptions(
  response: ImageOptionsResponse | undefined,
  selectedFileId: string | null,
  apiBase: string
): PickerOption[] {
  const options = response?.options ?? [];
  const out: PickerOption[] = options.map((o) => ({
    fileId: o.file_id,
    source: o.source,
    label: o.label,
    thumbUrl: thumb(apiBase, o.url),
    selected: o.file_id === selectedFileId,
  }));
  if (selectedFileId && !options.some((o) => o.file_id === selectedFileId)) {
    out.unshift({
      fileId: selectedFileId,
      source: "current",
      label: "Current",
      thumbUrl: thumb(apiBase, `/assets/${selectedFileId}`),
      selected: true,
    });
  }
  return out;
}

/** The PATCH a pick saves, or null when it changes nothing. */
export function imagePickPatch(
  currentFileId: string | null,
  pickedFileId: string | null
): { image_file_id: string | null } | null {
  if (currentFileId === pickedFileId) return null;
  return { image_file_id: pickedFileId };
}

/**
 * Are the thumbnails and "Remove image" locked? While a save is in flight, on
 * a read-only post, and while an upload is in flight: the upload ends in its
 * own pick, whose failure restores the image it started from — a click made
 * meanwhile would be silently reverted.
 */
export function imagePickerLocked(state: { readOnly: boolean; saving: boolean; uploading: boolean }): boolean {
  return state.readOnly || state.saving || state.uploading;
}
